import { useCallback, useEffect, useRef, useState } from "react";
import type { MediaItem } from "@gooner/shared";
import { clipKey } from "@gooner/shared";
import { track } from "../../analytics";

export type SelectEntry = "button" | "checkbox" | "long_press" | "keyboard";

/** The coarse device class the select-mode events carry: what the hand is, not the UA. */
export function selectDevice(): "desktop" | "mobile" | "tablet" {
  if (typeof window === "undefined" || !window.matchMedia("(pointer: coarse)").matches) return "desktop";
  return window.innerWidth < 768 ? "mobile" : "tablet";
}

export interface Selection {
  active: boolean;
  selected: ReadonlySet<string>;
  /** Did the last change select everything (so the action reports select_all)? */
  allSelected: boolean;
  enter: (entry: SelectEntry, first?: string) => void;
  /** Toggle one clip. With `range`, everything between the last toggled clip and this one
   *  takes this one's new state (Shift-click). */
  toggle: (key: string, range?: boolean) => void;
  selectAll: () => void;
  clear: () => void;
  exit: (reason: "action" | "cancel") => void;
}

/**
 * Select mode for a collection page (issue #12). Keyed by clipKey — the wire id the
 * bulk endpoint takes — and ordered by `items`, which is the whole collection (the page
 * loads it whole), so Select all really is all.
 *
 * While active: Esc leaves, Ctrl/Cmd+A selects all (and enters, from the keyboard), and
 * on a touch device the BACK gesture leaves instead of leaving the page — entering pushes
 * a same-URL history entry that a back pops. Desktop gets no entry: there the back button
 * is a navigation, and a page that takes two presses to leave is a trap.
 */
export function useSelection(items: MediaItem[] | null, enabled: boolean, paused = false): Selection {
  const [active, setActive] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const anchor = useRef<string | null>(null);
  const pushedHistory = useRef(false);
  const order = (items ?? []).map(clipKey);
  const orderRef = useRef(order);
  orderRef.current = order;
  const selectedRef = useRef(selected);
  selectedRef.current = selected;
  const activeRef = useRef(false);

  // A clip that left the page (moved, removed, reloaded away) leaves the selection too,
  // so the count can never claim one the grid no longer shows.
  const orderKey = order.join("\n");
  useEffect(() => {
    setSelected((prev) => {
      const live = new Set(orderRef.current);
      const next = new Set([...prev].filter((k) => live.has(k)));
      return next.size === prev.size ? prev : next;
    });
  }, [orderKey]);

  const finish = useCallback((reason: "action" | "cancel", fromPop = false) => {
    if (!activeRef.current) return;
    activeRef.current = false;
    track("collection_select_mode_exited", { reason, selected_count: selectedRef.current.size });
    setActive(false);
    setSelected(new Set());
    anchor.current = null;
    if (pushedHistory.current) {
      pushedHistory.current = false;
      // Take our own entry back off, unless the back gesture already did.
      if (!fromPop && (window.history.state as { gpSelect?: boolean } | null)?.gpSelect) window.history.back();
    }
  }, []);

  const enter = useCallback(
    (entry: SelectEntry, first?: string) => {
      if (!enabled) return;
      // Side effects outside the state updater: StrictMode runs an updater twice.
      if (!activeRef.current) {
        activeRef.current = true;
        track("collection_select_mode_entered", { entry, device: selectDevice() });
        if (selectDevice() !== "desktop") {
          // Spread the router's own state (its idx/key) so react-router reads this as
          // the same location; `gpSelect` is how a back gesture is recognised as ours.
          window.history.pushState({ ...(window.history.state ?? {}), gpSelect: true }, "");
          pushedHistory.current = true;
        }
        setActive(true);
      }
      if (first) {
        anchor.current = first;
        setSelected((s) => new Set(s).add(first));
      }
    },
    [enabled],
  );

  const toggle = useCallback((key: string, range = false) => {
    // Read the anchor NOW: the updater below runs later, after it has moved on to `key`.
    const from = anchor.current;
    anchor.current = key;
    setSelected((prev) => {
      const next = new Set(prev);
      const on = !prev.has(key);
      const keys = orderRef.current;
      const a = from ? keys.indexOf(from) : -1;
      const b = keys.indexOf(key);
      if (range && a >= 0 && b >= 0) {
        for (const k of keys.slice(Math.min(a, b), Math.max(a, b) + 1)) {
          if (on) next.add(k);
          else next.delete(k);
        }
      } else if (on) next.add(key);
      else next.delete(key);
      return next;
    });
  }, []);

  const selectAll = useCallback(() => setSelected(new Set(orderRef.current)), []);
  const clear = useCallback(() => {
    setSelected(new Set());
    anchor.current = null;
  }, []);

  useEffect(() => {
    // Paused while the page has a dialog up (the Remove confirm, the Gold upsell): their
    // Escape and Enter are theirs.
    if (!enabled || paused) return;
    const typing = (t: EventTarget | null) =>
      t instanceof HTMLElement && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName));
    const onKey = (e: KeyboardEvent) => {
      if (typing(e.target)) return;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "a" && orderRef.current.length > 0) {
        e.preventDefault();
        enter("keyboard");
        selectAll();
      } else if (e.key === "Escape" && active) {
        // An open picker (role=dialog) takes its own Escape first.
        if (document.querySelector("[role=dialog]")) return;
        finish("cancel");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [enabled, paused, active, enter, selectAll, finish]);

  useEffect(() => {
    if (!active) return;
    const onPop = () => {
      if (pushedHistory.current && !(window.history.state as { gpSelect?: boolean } | null)?.gpSelect) {
        finish("cancel", true);
      }
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [active, finish]);

  // Leaving the page in select mode (a link, not a back) leaves nothing behind.
  useEffect(() => () => void (pushedHistory.current = false), []);

  return {
    active,
    selected,
    allSelected: order.length > 0 && selected.size === order.length,
    enter,
    toggle,
    selectAll,
    clear,
    exit: finish,
  };
}
